import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  MdClose,
  MdCheckCircle,
  MdWarning,
  MdInfo,
  MdError,
  MdTrendingUp,
  MdCalendarToday
} from "react-icons/md";
import { FaShieldAlt } from "react-icons/fa";
import axios from "../../utils/api/axios";
import { useGetProgramQuery } from "../../utils/api/endpoints/programsApi";
import { useGetProgramRunsQuery } from "../../utils/api/endpoints/programRunsApi";

interface ReportItem {
  report_id: number;
  report_title: string;
  severity_category: string;
  severity: number | string; // CVSS score (can be numeric or vector string)
  cvss_score?: number;
  cvss_vector?: string;
  cvss_version?: string;
  computed_severity_category: string; // Dynamically computed severity category from backend
  state: string;
  category: string;
  retest_status: string | null;
  is_closed: boolean;
}

interface ReportStats {
  total: number;
  closed: number;
  open: number;
  businessAcceptRisk: number;
  nonActionable: number;
  awaitingFix: number;
  fixVerified: number;
  fixApproved: number;
  closeRetest: number;
  severityBreakdown: {
    critical: number;
    high: number;
    medium: number;
    low: number;
    info: number;
  };
  reports: ReportItem[];
}

interface PentestReportConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (runId?: number) => void;
  programId: number;
  nextVersion?: number;
  isNewVersion?: boolean;
  hasApprovedReport?: boolean;
  fixedRunId?: number;
}

const PentestReportConfirmationModal: React.FC<PentestReportConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  programId,
  nextVersion = 1,
  isNewVersion = false,
  hasApprovedReport = false,
  fixedRunId
}) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<ReportStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [highlightCheckbox, setHighlightCheckbox] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState<number | undefined>(fixedRunId);
  const checkboxRef = React.useRef<HTMLDivElement>(null);

  // Fetch program to check if it's continuous
  const { data: program } = useGetProgramQuery(programId, { skip: !programId });
  const isContinuous = program?.scheduling_type === 'continuous';

  // Fetch runs if continuous
  const { data: runs = [], isLoading: isLoadingRuns } = useGetProgramRunsQuery(
    { program_id: programId.toString() },
    { skip: !programId || !isContinuous }
  );

  const fetchReportStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `/v2/reports/stats/${programId}${selectedRunId ? `?run_id=${selectedRunId}` : ""}`;
      const response = await axios.get(url);
      setStats(response.data);
    } catch (err: any) {
      console.error("Error fetching report stats:", err);
      setError("Failed to load report statistics");
    } finally {
      setLoading(false);
    }
  }, [programId, selectedRunId]);

  useEffect(() => {
    if (isOpen && programId) {
      // Synchronize the selectedRunId immediately to prevent stale stats fetches
      setSelectedRunId(fixedRunId);
      setStats(null); // Clear previous stats to avoid showing stale data during loading
      setIsConfirmed(false); // Reset checkbox when modal opens
      setHighlightCheckbox(false); // Reset highlight when modal opens
    }
  }, [isOpen, programId, fixedRunId]);

  useEffect(() => {
    if (isOpen && programId) {
      fetchReportStats();
    }
  }, [isOpen, programId, fetchReportStats]);

  // Check if we should show the detailed modal or simple confirmation
  // Show detailed modal if there are any reports that are not fully closed
  const shouldShowDetailedModal = stats && stats.total > 0 && (stats.open > 0 || stats.businessAcceptRisk > 0 || stats.nonActionable > 0);

  // If no reports or all reports are fully resolved (closed), show simple confirmation
  if (!loading && !shouldShowDetailedModal) {
    return (
      <div
        className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-300 ${isOpen ? "visible opacity-100" : "invisible opacity-0"
          }`}
      >
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
        <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
          <h2 className="mb-4 text-xl font-bold text-gray-900">
            {hasApprovedReport ? `Request Version ${nextVersion}` : "Request Pentest Report"}
          </h2>
          <p className="mb-6 text-gray-600">
            {hasApprovedReport
              ? `Are you sure you want to request Version ${nextVersion} for this program? This will create a new report record for manual review and approval.`
              : "Are you sure you want to request a pentest report for this program? This will initiate the report generation process for manual review and approval."}
          </p>
          <div className="mb-6 rounded-lg bg-blue-50 p-4 border border-blue-200 text-sm text-blue-800">
            <strong>Notice:</strong> The report will be available 24–48 hours after the QA is completed.
          </div>
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => onConfirm(selectedRunId)}
              className="rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition-all hover:from-blue-700 hover:to-blue-800"
            >
              {hasApprovedReport ? `Yes, Request Version ${nextVersion}` : "Yes, Request"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!isOpen) return null;

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-600 bg-red-50';
      case 'high': return 'text-orange-600 bg-orange-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'low': return 'text-blue-600 bg-blue-50';
      case 'info': return 'text-gray-600 bg-gray-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };



  // Helper function to dynamically determine severity category (like status pills)
  const getDynamicSeverityCategory = (report: ReportItem) => {
    // Use computed severity category from backend if present, with fallback calculation
    let category = (report.computed_severity_category || report.severity_category || '').toLowerCase();

    if (!category || category === 'info' || category === 'none') {
      const score = report.cvss_score !== undefined && report.cvss_score !== null
        ? Number(report.cvss_score)
        : (typeof report.severity === 'number'
          ? report.severity
          : (typeof report.severity === 'string' && !report.severity.startsWith('CVSS:') && !isNaN(parseFloat(report.severity))
            ? parseFloat(report.severity)
            : null));

      if (score !== null && !isNaN(score)) {
        if (score >= 9.0) category = 'critical';
        else if (score >= 7.0) category = 'high';
        else if (score >= 4.0) category = 'medium';
        else if (score > 0) category = 'low';
        else category = 'info';
      }
    }

    if (!category) category = 'info';

    return {
      label: category,
      color: category === 'critical' ? 'bg-red-100 text-red-800' :
        category === 'high' ? 'bg-orange-100 text-orange-800' :
          category === 'medium' ? 'bg-yellow-100 text-yellow-800' :
            category === 'low' ? 'bg-blue-100 text-blue-800' :
              'bg-gray-100 text-gray-800'
    };
  };

  // Helper function to categorize report status
  const getCategorizedStatus = (report: ReportItem) => {
    // Use the is_closed flag from backend
    if (report.is_closed) {
      return { label: "Closed", color: "bg-green-100 text-green-800" };
    }

    const state = (report.state || "").trim().toLowerCase();

    // Check if Business Accepts Risk
    if (state === "business accepts risk") {
      return { label: "Accept Risk", color: "bg-yellow-100 text-yellow-800" };
    }

    // Check if Non-Actionable
    if (state === "business non actionable issue") {
      return { label: "Non-Actionable", color: "bg-gray-100 text-gray-800" };
    }

    // Otherwise it's Open
    return { label: "Open", color: "bg-orange-100 text-orange-800" };
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative z-50 w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-white/20 p-2">
                <FaShieldAlt className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  {isNewVersion ? `Request New Pentest Report Version ${nextVersion}` : "Request Pentest Report"}
                </h2>
                <p className="text-sm text-blue-100">
                  Review report statistics before proceeding
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-2 text-white/80 transition-colors hover:bg-white/20 hover:text-white"
            >
              <MdClose className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="h-12 w-12 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600" />
              <p className="mt-4 text-sm text-gray-600">Loading report statistics...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <MdError className="h-12 w-12 text-red-500" />
              <p className="mt-4 text-sm text-gray-600">{error}</p>
            </div>
          ) : stats ? (
            <div className="space-y-6">
              {/* Run Selector for Continuous Programs */}
              {isContinuous && runs.length > 0 && (
                <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-4">
                  <div className="flex items-center gap-2 text-blue-800 mb-3">
                    <MdCalendarToday className="h-5 w-5" />
                    <span className="text-sm font-semibold">
                      {fixedRunId !== undefined ? "Program Run" : "Select Program Run"}
                    </span>
                  </div>
                  <div className="relative">
                    {fixedRunId !== undefined ? (
                      <div className="w-full rounded-lg border border-blue-300 bg-white px-4 py-2.5 text-sm text-gray-900 font-medium">
                        Run #{fixedRunId} {runs.find((r: any) => r.run_id === fixedRunId)?.run_title ? `- ${runs.find((r: any) => r.run_id === fixedRunId).run_title}` : ""}
                      </div>
                    ) : (
                      <select
                        value={selectedRunId || ""}
                        onChange={(e) => setSelectedRunId(e.target.value ? parseInt(e.target.value) : undefined)}
                        className="w-full rounded-lg border border-blue-300 bg-white px-4 py-2.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Full Program (All Runs)</option>
                        {runs.map((run: any) => (
                          <option key={run.run_id} value={run.run_id}>
                            Run #{run.run_id} {run.run_title ? `- ${run.run_title}` : ""}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <p className="mt-2 text-xs text-blue-600">
                    {fixedRunId !== undefined
                      ? "The findings and statistics below are specifically for this run."
                      : "Choosing a run will filter the findings and statistics for this specific period."}
                  </p>
                </div>
              )}

              {/* Status Cards - 4 Cards */}
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-green-50 to-green-100/50 p-4">
                  <div className="flex items-center gap-2 text-green-700">
                    <MdCheckCircle className="h-5 w-5" />
                    <span className="text-sm font-medium">Closed</span>
                  </div>
                  <p className="mt-2 text-3xl font-bold text-green-900">{stats.closed}</p>
                </div>

                <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-orange-50 to-orange-100/50 p-4">
                  <div className="flex items-center gap-2 text-orange-700">
                    <MdWarning className="h-5 w-5" />
                    <span className="text-sm font-medium">Open</span>
                  </div>
                  <p className="mt-2 text-3xl font-bold text-orange-900">{stats.open}</p>
                </div>

                <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-yellow-50 to-yellow-100/50 p-4">
                  <div className="flex items-center gap-2 text-yellow-700">
                    <MdInfo className="h-5 w-5" />
                    <span className="text-sm font-medium">Accept Risk</span>
                  </div>
                  <p className="mt-2 text-3xl font-bold text-yellow-900">{stats.businessAcceptRisk}</p>
                </div>

                <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-gray-50 to-gray-100/50 p-4">
                  <div className="flex items-center gap-2 text-gray-700">
                    <MdInfo className="h-5 w-5" />
                    <span className="text-sm font-medium">Non-Actionable</span>
                  </div>
                  <p className="mt-2 text-3xl font-bold text-gray-900">{stats.nonActionable}</p>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-4">
                <div className="flex gap-3">
                  <MdWarning className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
                  <div className="text-sm text-amber-900">
                    <p className="font-semibold mb-1">Important Notice</p>
                    <p>
                      The status of the reports you are seeing here will be reflected in the pentest report.
                      If you want, you can close all the remaining reports and come back later to request a pentest report.
                    </p>
                  </div>
                </div>
              </div>

              {/* Reports Table */}
              <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                <div className="bg-gray-50 px-5 py-3 border-b border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900">Reports List ({stats.total} total)</h3>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-[45%]">
                          Report Title
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-[25%]">
                          Severity
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-[30%]">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {stats.reports && stats.reports.length > 0 ? (
                        stats.reports.map((report) => (
                          <tr key={report.report_id} className="hover:bg-gray-50 cursor-pointer transition-colors">
                            <td
                              className="px-4 py-3 text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium"
                              onClick={() => navigate(`/dashboard/reports/${report.report_id}`)}
                            >
                              {report.report_title || `Report #${report.report_id}`}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {(() => {
                                const severity = getDynamicSeverityCategory(report);
                                return (
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${severity.color}`}>
                                    {severity.label}
                                  </span>
                                );
                              })()}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {(() => {
                                const status = getCategorizedStatus(report);
                                return (
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                                    {status.label}
                                  </span>
                                );
                              })()}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={3} className="px-4 py-8 text-center text-sm text-gray-500">
                            No reports found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Severity Breakdown */}
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
                <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <MdTrendingUp className="h-5 w-5 text-gray-600" />
                  Severity Breakdown
                </h3>
                <div className="grid grid-cols-5 gap-3">
                  {Object.entries(stats.severityBreakdown).map(([severity, count]) => (
                    <div
                      key={severity}
                      className={`rounded-lg border p-3 text-center ${getSeverityColor(severity)}`}
                    >
                      <p className="text-2xl font-bold">{count}</p>
                      <p className="mt-1 text-xs font-medium capitalize">{severity}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Guidance */}
              <div className="rounded-xl border-2 border-blue-200 bg-blue-50 p-5">
                <div className="flex gap-3">
                  <MdInfo className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
                  <div className="space-y-3 text-sm text-blue-900">
                    <p className="font-semibold text-base">How to Close Reports Properly:</p>
                    <div className="space-y-2 text-blue-800">
                      <div className="flex gap-2">
                        <span className="font-semibold">1.</span>
                        <p><span className="font-semibold">Approve the Report:</span> Review and approve the vulnerability report from your dashboard</p>
                      </div>
                      <div className="flex gap-2">
                        <span className="font-semibold">2.</span>
                        <p><span className="font-semibold">Change Status to "Fixing the Vulnerability":</span> Mark the report as "Fixing the Vulnerability" to indicate you're working on it</p>
                      </div>
                      <div className="flex gap-2">
                        <span className="font-semibold">3.</span>
                        <p><span className="font-semibold">Patch the Vulnerability:</span> Have your development team fix the security issue</p>
                      </div>
                      <div className="flex gap-2">
                        <span className="font-semibold">4.</span>
                        <p><span className="font-semibold">Request Retest:</span> Once fixed, request a retest from the report page</p>
                      </div>
                      <div className="flex gap-2">
                        <span className="font-semibold">5.</span>
                        <p><span className="font-semibold">Verification:</span> Our pentesting team will retest and mark it as "Fix Verified" if the issue is resolved</p>
                      </div>
                      <div className="mt-3 rounded-lg bg-blue-100 p-3">
                        <p className="font-semibold text-blue-900">Alternative Options:</p>
                        <ul className="ml-4 mt-1 list-disc space-y-1">
                          <li><span className="font-medium">"Business Accepts Risk"</span> - If you choose not to fix the vulnerability</li>
                          <li><span className="font-medium">"Non-Actionable Issue"</span> - If the report doesn't require action</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Confirmation Checkbox */}
              <div
                ref={checkboxRef}
                className={`mx-6 mb-6 flex flex-col items-start gap-3 rounded-lg border-2 p-4 transition-all duration-300 ${highlightCheckbox
                  ? 'border-red-500 bg-red-50 shadow-lg'
                  : 'border-blue-200 bg-blue-50'
                  }`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="confirmData"
                    checked={isConfirmed}
                    onChange={(e) => {
                      setIsConfirmed(e.target.checked);
                      if (e.target.checked) {
                        setHighlightCheckbox(false);
                      }
                    }}
                    className="mt-1 h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
                  />
                  <label htmlFor="confirmData" className="cursor-pointer text-sm text-gray-700">
                    <span className="font-semibold text-gray-900">I confirm that</span> I have reviewed all the report data shown above and I am satisfied with the current status of all reports. I understand that the pentest report will be generated based on this information.
                  </label>
                </div>
                <div className="mt-2.5 text-xs text-blue-800 bg-blue-100/50 border border-blue-200 rounded p-2.5 font-medium w-full">
                  <strong>Notice:</strong> The report will be available 24–48 hours after the QA is completed.
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 border-t border-gray-200 bg-gray-50 px-6 py-4">
          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (!isConfirmed) {
                  // Scroll to checkbox and highlight it
                  setHighlightCheckbox(true);
                  checkboxRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                  });

                  // Remove highlight after 3 seconds
                  setTimeout(() => {
                    setHighlightCheckbox(false);
                  }, 3000);
                  return;
                }

                onConfirm(selectedRunId);
                onClose();
              }}
              disabled={loading}
              className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400"
            >
              {hasApprovedReport ? `Request Version ${nextVersion}` : "Request Pentest Report"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PentestReportConfirmationModal;

